import Ship from "@/assets/icons/ship.svg?react";
import { StateMessage } from "@/ds";
import { HomeCard } from "../home-card";
import { HomeChart } from "../home-chart";
import styles from "./obras-view.module.scss";
import type { ObraDestaque, ObrasViewViewModel } from "./types-obras-view";

/** Tarja de identificação da Obra: número, período, duração e embarcação. */
function ObraChips({ obra }: { obra: ObraDestaque }) {
  return (
    <div className={styles.chips}>
      <span className={styles.chip}>ID {obra.id}</span>

      <span className={`${styles.chip} ${styles.chipPeriodo}`}>
        <i className={`pi pi-calendar ${styles.iconCalendario}`} aria-hidden />

        <span className={styles.periodo}>
          <span>{obra.inicio}</span>
          <span aria-hidden>→</span>
          <span>{obra.termino}</span>
        </span>

        <span className={styles.duracao}>
          <i className={`pi pi-clock ${styles.iconRelogio}`} aria-hidden />
          <span>{obra.duracao}</span>
        </span>
      </span>

      {/* Terceira tarja do protótipo, só com o ícone: identifica que a Obra é de
          embarcação. A modalidade ainda não faz parte do contrato de dados, por
          isso o ícone é fixo até que as variações sejam definidas. */}
      <span className={`${styles.chip} ${styles.chipIcone}`} data-testid="home-obra-embarcacao">
        <Ship className={styles.iconNavio} role="img" aria-label="Obra de embarcação" />
      </span>
    </div>
  );
}

/**
 * Cartão Financeiro da Obra: valor total, curva de desembolso com o marcador
 * "Hoje" e a participação de cada fase (AC5/AC6).
 */
function FinanceiroCard({ financeiro }: { financeiro: ObraDestaque["financeiro"] }) {
  return (
    <HomeCard title="Financeiro" className={styles.financeiro} testId="home-card-financeiro">
      <div className={styles.financeiroHeadline}>
        <strong className={styles.financeiroValor} data-testid="home-financeiro-valor">
          {financeiro.valor}
        </strong>

        <div className={styles.financeiroFel}>
          <span className={styles.felTag}>{financeiro.fel}</span>
          <span className={styles.felLegenda}>{financeiro.legenda}</span>
        </div>
      </div>

      <HomeChart
        points={financeiro.serie}
        ariaLabel="Curva de desembolso da Obra, em milhões de reais"
        labelHeader="Período"
        valueHeader="Desembolso"
        formatValue={financeiro.formatValue}
        borderWidth={0}
        markerIndex={financeiro.indiceHoje}
        showGrid={false}
        className={styles.financeiroChart}
        testId="home-chart-financeiro"
      />

      <ul className={styles.fases}>
        {financeiro.fases.map((fase) => (
          <li key={fase.codigo} className={styles.fase} data-testid={fase.testId}>
            <span className={styles.faseSigla}>{fase.sigla}</span>
            <span className={styles.fasePercentual}>{fase.percentual}</span>
            <span className={styles.faseCodigo}>{fase.codigo}</span>
          </li>
        ))}
      </ul>
    </HomeCard>
  );
}

/** Cartão Obra: avanço dos escopos da Obra em execução. */
function ObraProgressoCard({ progresso }: { progresso: ObraDestaque["progresso"] }) {
  return (
    <HomeCard title="Obra" className={styles.progresso} testId="home-card-obra">
      <ul className={styles.progressoList}>
        {progresso.map((item) => (
          <li key={item.label} className={styles.progressoItem}>
            <div className={styles.progressoNumbers}>
              <strong className={styles.progressoPercentual}>{item.percentual}</strong>
              <span className={styles.progressoFracao}>{item.fracao}</span>
            </div>

            <span className={styles.progressoLabel}>{item.label}</span>
          </li>
        ))}
      </ul>
    </HomeCard>
  );
}

/** Cartão GMUD: aprovações e contagem por natureza da mudança. */
function GmudCard({ gmud }: { gmud: ObraDestaque["gmud"] }) {
  return (
    <HomeCard title="GMUD" className={styles.gmud} testId="home-card-gmud">
      <div className={styles.gmudMobileHeadline}>
        <h2 className={styles.gmudMobileTitle}>GMUD</h2>
        <div className={styles.gmudHeadline}>
          <strong className={styles.gmudAprovadas}>{gmud.aprovadas}</strong>
          <span className={styles.gmudTotal}>{gmud.total}</span>
          <span className={styles.gmudLegenda}>Aprovadas</span>
        </div>
      </div>

      <ul className={styles.gmudGrid}>
        {gmud.naturezas.map((natureza) => (
          <li key={natureza.key} className={styles.gmudItem} data-testid={natureza.testId}>
            <span className={styles.gmudItemValue}>
              <i className={`${natureza.icon} ${styles.gmudIcon}`} aria-hidden />
              {natureza.valor}
            </span>
            <span className={styles.gmudItemLabel}>{natureza.label}</span>
          </li>
        ))}
      </ul>
    </HomeCard>
  );
}

/**
 * Visão Obras em Andamento: um destaque por Obra em execução, percorrido pelos
 * controles do rodapé, como no protótipo (AC2).
 */
export function ObrasViewView({
  obra,
  temVarias,
  indicadores,
  irParaObra,
  obraAnterior,
  proximaObra,
}: ObrasViewViewModel) {
  if (!obra) {
    return (
      <StateMessage variant="empty" title="Nenhuma obra em andamento." testId="home-obras-vazio" />
    );
  }

  return (
    <div className={styles.container} data-testid="home-obras-em-andamento">
      <article className={styles.destaque} data-testid={obra.testId}>
        <header className={styles.destaqueHeader}>
          <ObraChips obra={obra} />
          <h2 className={styles.chamada}>{obra.chamada}</h2>
          <p className={styles.descricao}>{obra.descricao}</p>
        </header>

        <div className={styles.cards}>
          <FinanceiroCard financeiro={obra.financeiro} />
          <ObraProgressoCard progresso={obra.progresso} />
          <GmudCard gmud={obra.gmud} />
        </div>

        {/* No protótipo o acesso fica acima dos indicadores do carrossel, à
            esquerda, e as setas ficam sozinhas no canto direito. */}
        <footer className={styles.destaqueFooter}>
          <div className={styles.footerStart}>
            {/* As rotas de Obra ainda não existem (`field.routes.tsx` está
                vazio): o acesso aparece como no protótipo, porém desabilitado,
                para não oferecer um destino que a aplicação não tem. */}
            <button
              type="button"
              className={styles.acessar}
              disabled
              title="Disponível quando o módulo de Obras for liberado no Hub."
              data-testid="home-obra-acessar"
            >
              Acessar Obra
              <i className="pi pi-arrow-right" aria-hidden />
            </button>

            {temVarias ? (
              <div className={styles.dots} role="tablist" aria-label="Obras em Andamento">
                {indicadores.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    role="tab"
                    className={[styles.dot, item.ativo ? styles.dotActive : ""]
                      .filter(Boolean)
                      .join(" ")}
                    aria-selected={item.ativo}
                    aria-label={item.label}
                    onClick={() => irParaObra(item.id)}
                    data-testid={item.testId}
                  />
                ))}
              </div>
            ) : null}
          </div>

          {temVarias ? (
            <div className={styles.arrows}>
              <button
                type="button"
                className={styles.arrow}
                aria-label="Obra anterior"
                onClick={obraAnterior}
                data-testid="home-obra-anterior"
              >
                <i className="pi pi-chevron-left" aria-hidden />
              </button>

              <button
                type="button"
                className={styles.arrow}
                aria-label="Próxima Obra"
                onClick={proximaObra}
                data-testid="home-obra-proxima"
              >
                <i className="pi pi-chevron-right" aria-hidden />
              </button>
            </div>
          ) : null}
        </footer>
      </article>
    </div>
  );
}
